import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { wabaApi } from "../../lib/wabaApi";
import { XIcon } from "../shared/Icons";
import { Modal } from "../shared/Modal";
import { ConfirmDialog } from "../common/ConfirmDialog";

interface Webhook { id: string; name: string; url: string; active: boolean; event_types: string[] | null; mode: string; schema_version: number; }

const EVENT_TYPES: { key: string; label: string }[] = [
  { key: "messages", label: "Mensagem de Lead" },
  { key: "errors", label: "Erros" },
  { key: "echoes", label: "Echoes" },
  { key: "outbound", label: "Carga de Envios" },
  { key: "calls", label: "Chamadas" },
  { key: "groups", label: "Grupos" },
];
// v3 filtra por "type.event" (contrato universal)
const EVENT_TYPES_V3: { key: string; label: string }[] = [
  { key: "inbound.message", label: "Mensagem recebida" },
  { key: "inbound.reaction", label: "Reação recebida" },
  { key: "inbound.call", label: "Ligação recebida" },
  { key: "inbound.comment", label: "Comentário" },
  { key: "inbound.identity", label: "Troca de número/ID" },
  { key: "inbound.preference", label: "Preferência de marketing" },
  { key: "outbound.message", label: "Mensagem enviada" },
  { key: "outbound.reaction", label: "Reação enviada" },
  { key: "outbound.call", label: "Ligação feita" },
  { key: "outbound.comment", label: "Resposta a comentário" },
  { key: "system.status", label: "Status de entrega" },
  { key: "system.conversion", label: "Conversão de anúncio" },
  { key: "system.template", label: "Aviso de template (Meta)" },
  { key: "system.restriction", label: "Restrição da conta (Meta)" },
  { key: "inbound.group", label: "Grupo: mensagem, entrada e saída" },
  { key: "outbound.group", label: "Grupo: mensagem enviada" },
  { key: "system.group", label: "Grupo: criação, status e avisos" },
];
const eventLabel = (k: string) => [...EVENT_TYPES, ...EVENT_TYPES_V3].find(e => e.key === k)?.label ?? k;

interface Props { connectionId: string; }

export function OutboundWebhooks({ connectionId }: Props) {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", url: "" });
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [mode, setMode] = useState<"custom" | "meta">("custom");
  const [schemaVersion, setSchemaVersion] = useState<1 | 2 | 3>(1);
  const [error, setError] = useState<string | null>(null);

  const closeForm = () => { setCreating(false); setEditingId(null); setForm({ name: "", url: "" }); setEventTypes([]); setMode("custom"); setSchemaVersion(1); setError(null); };
  const startEdit = (wh: Webhook) => { setCreating(true); setEditingId(wh.id); setForm({ name: wh.name, url: wh.url }); setEventTypes(wh.event_types ?? []); setMode(wh.mode === "meta" ? "meta" : "custom"); setSchemaVersion(wh.schema_version === 3 ? 3 : wh.schema_version === 2 ? 2 : 1); setError(null); };

  const { data: webhooks = [] } = useQuery<Webhook[]>({
    queryKey: ["waba-outbound-webhooks", connectionId],
    queryFn: () => wabaApi.get(`/connections/${connectionId}/outbound-webhooks`),
  });

  const createMut = useMutation({
    mutationFn: () => {
      // v2: mode não se aplica (o envelope já embute a carga da Meta; demais
      // eventos saem sempre no formato custom) — grava "custom" por higiene.
      const effMode = schemaVersion === 1 ? mode : "custom";
      const body = { ...form, mode: effMode, schema_version: schemaVersion, event_types: effMode === "custom" && eventTypes.length ? eventTypes : null };
      return editingId
        ? wabaApi.patch(`/connections/${connectionId}/outbound-webhooks/${editingId}`, body)
        : wabaApi.post(`/connections/${connectionId}/outbound-webhooks`, body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["waba-outbound-webhooks", connectionId] }); closeForm(); },
    onError: (e: any) => setError(e?.payload?.detail ?? "Erro ao salvar webhook."),
  });

  const [deletingWh, setDeletingWh] = useState<Webhook | null>(null);

  const deleteMut = useMutation({
    mutationFn: (id: string) => wabaApi.delete(`/connections/${connectionId}/outbound-webhooks/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["waba-outbound-webhooks", connectionId] }),
  });

  const inputStyle: React.CSSProperties = { padding: "6px 10px", background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 6, color: "var(--ink)", fontSize: 12, outline: "none" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 11, color: "var(--ink-muted)", marginBottom: 2 }}>
        Outbound Webhooks ({webhooks.length}/5) — cópias de todas as cargas recebidas e enviadas
      </div>

      {webhooks.map(wh => (
        <div key={wh.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "var(--surface-raised)", border: "1px solid var(--line)", borderRadius: 6 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, color: "var(--ink)" }}>{wh.name}</div>
            <div style={{ fontSize: 11, color: "var(--ink-dim)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{wh.url}</div>
            <div style={{ fontSize: 10, color: "var(--ink-muted)" }}>
              {wh.schema_version >= 2 ? `v${wh.schema_version} · ` : ""}
              {wh.schema_version === 1 && wh.mode === "meta" ? "Original Meta" : wh.event_types?.length ? wh.event_types.map(eventLabel).join(" · ") : "Todas as cargas"}
            </div>
          </div>
          {!wh.active && <span style={{ fontSize: 10, color: "var(--ink-muted)" }}>pausado</span>}
          <button onClick={() => startEdit(wh)} title="Editar" style={{ background: "transparent", border: "none", color: "var(--ink-dim)", fontSize: 12, cursor: "pointer" }}>✎</button>
          <button onClick={() => setDeletingWh(wh)} aria-label="Remover" style={{ background: "transparent", border: "none", color: "var(--brand)", fontSize: 13, cursor: "pointer" }}><XIcon size={13} /></button>
        </div>
      ))}

      {webhooks.length < 5 && !creating && (
        <button onClick={() => setCreating(true)} style={{ background: "transparent", border: "1px dashed var(--line-bold)", borderRadius: 6, color: "var(--ink-dim)", fontSize: 12, padding: "7px 0", cursor: "pointer" }}>
          + Adicionar webhook
        </button>
      )}

      {creating && (
        <Modal onClose={closeForm}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 20, width: 420, maxWidth: "100%", background: "var(--surface-card)", border: "1px solid var(--line-strong)", borderRadius: 10, boxShadow: "var(--shadow-modal)" }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>{editingId ? "Editar webhook" : "Adicionar webhook"}</span>
          <input style={{ ...inputStyle, width: "100%" }} placeholder="Nome (ex: CRMillion)" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <input style={{ ...inputStyle, width: "100%" }} placeholder="URL (https://...)" value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} />
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            {([[1, "v1 (legado)"], [2, "v2 (envelope)"], [3, "v3 (universal)"]] as const).map(([value, label]) => (
              <label key={value} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "var(--ink-secondary)", cursor: "pointer" }}>
                {/* filtros da v3 (type.event) não valem nas outras versões e vice-versa */}
                <input type="radio" name="wh-version" checked={schemaVersion === value} onChange={() => { if ((value === 3) !== (schemaVersion === 3)) setEventTypes([]); setSchemaVersion(value); }} />
                {label}
              </label>
            ))}
          </div>
          {schemaVersion === 2 && (
            <span style={{ fontSize: 10, color: "var(--ink-muted)" }}>
              Mensagens, echoes e envios chegam padronizados (lead, tipo, mídia, ads, quote) + carga original em `meta`. Demais eventos (erros, chamadas) seguem no formato v1 enriquecido.
            </span>
          )}
          {schemaVersion === 1 && (
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              {([["custom", "Custom"], ["meta", "Original Meta"]] as const).map(([value, label]) => (
                <label key={value} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "var(--ink-secondary)", cursor: "pointer" }}>
                  <input type="radio" name="wh-mode" checked={mode === value} onChange={() => setMode(value)} />
                  {label}
                </label>
              ))}
            </div>
          )}
          {schemaVersion === 3 && (
            <span style={{ fontSize: 10, color: "var(--ink-muted)" }}>
              Um POST por evento em qualquer canal (WhatsApp, Instagram, Messenger, Facebook): lead, bloco do evento e a carga original da Meta em `meta`. Recusado ou expirado fica 7 dias para reenvio.
            </span>
          )}
          {schemaVersion !== 1 || mode === "custom" ? (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
                {(schemaVersion === 3 ? EVENT_TYPES_V3 : EVENT_TYPES).map(et => (
                  <label key={et.key} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "var(--ink-secondary)", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={eventTypes.includes(et.key)}
                      onChange={e => setEventTypes(prev => e.target.checked ? [...prev, et.key] : prev.filter(k => k !== et.key))}
                    />
                    {et.label}
                  </label>
                ))}
              </div>
              <span style={{ fontSize: 10, color: "var(--ink-muted)" }}>Nenhuma caixa marcada = recebe todas as cargas.</span>
            </>
          ) : (
            <span style={{ fontSize: 10, color: "var(--ink-muted)" }}>Carga crua da Meta, sem enriquecimentos nem eventos internos.</span>
          )}
          {error && <span style={{ fontSize: 11, color: "var(--danger)" }}>{error}</span>}
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => createMut.mutate()} disabled={!form.name || !form.url || createMut.isPending} style={{ background: "var(--brand)", color: "#fff", border: "none", borderRadius: 6, padding: "6px 14px", fontSize: 12, cursor: "pointer" }}>
              {createMut.isPending ? "Salvando..." : editingId ? "Salvar alterações" : "Salvar"}
            </button>
            <button onClick={closeForm} style={{ background: "transparent", color: "var(--ink-secondary)", border: "1px solid var(--line-bold)", borderRadius: 6, padding: "6px 12px", fontSize: 12, cursor: "pointer" }}>
              Cancelar
            </button>
          </div>
        </div>
        </Modal>
      )}

      {deletingWh && (
        <ConfirmDialog
          title="Remover webhook"
          message={`Remover "${deletingWh.name}"? A URL deixa de receber cargas imediatamente.`}
          confirmLabel="Remover"
          danger
          onConfirm={() => { deleteMut.mutate(deletingWh.id); setDeletingWh(null); }}
          onCancel={() => setDeletingWh(null)}
        />
      )}
    </div>
  );
}
